import React, { useState } from 'react'
import {
  DomainsToProductsCard,
  DomainsToProductsCardRow,
  DomainsToProductsContainer,
  MessageBoxContainer,
} from './MultipleDomains.styles'
import {
  Button,
  ButtonWithIcon,
  ModalControlsStyled,
  StyledText,
  Toast,
} from '@clickcease/cc-client-components-lib'
import { SelectOption } from 'Types/Option'
import { Trans, useTranslation } from 'react-i18next'
import { Plus } from 'Assets/icons/Plus'
import { SelectProduct } from '../features/SelectProduct'
import { useAddDomain } from 'Features/domainsManagement/api/addDomain'
import { DomainToServer } from 'Types/Domain'
import { useAllowedLimits } from 'src/hooks/useAllowedLimits'
import { Products } from 'Types/Product'

const sortDomains = (a: DomainToServer, b: DomainToServer) => a.address.localeCompare(b.address)

export const DomainToProduct = ({
  onOpenChange,
  domains,
}: {
  domains: SelectOption[]
  onOpenChange: (a: boolean | number) => void
}) => {
  const { t } = useTranslation('domainManagement')
  const { mutate, isLoading } = useAddDomain()
  const { domainsLeft, allowedProducts } = useAllowedLimits()

  const [domainsWithProducts, setDomainsWithProducts] = useState<DomainToServer[]>(() =>
    domains
      .map((domain) => ({
        ...allowedProducts,
        address: domain.value,
      }))
      .sort(sortDomains)
  )
  const amountOfProductsSelected = domainsWithProducts.reduce(
    (acc, domain) => ({
      paidMarketing: acc.paidMarketing + (domain.paidMarketing ? 1 : 0),
      botZapping: acc.botZapping + (domain.botZapping ? 1 : 0),
    }),
    { paidMarketing: 0, botZapping: 0 }
  )

  const noDomainsSelected = domainsWithProducts.length <= 0

  const paidMarketingLimitExceeded =
    allowedProducts.paidMarketing &&
    domainsLeft.paidMarketing < amountOfProductsSelected.paidMarketing

  const botZappingLimitExceeded =
    allowedProducts.botZapping && domainsLeft.botZapping < amountOfProductsSelected.botZapping

  const productsLimitExceeded = paidMarketingLimitExceeded || botZappingLimitExceeded

  // filter out products that not allowed
  const blockedDomains = Object.keys(allowedProducts).filter(
    (product) => !allowedProducts[product as Products]
  ) as Products[]

  const addDomains = () => {
    mutate(
      {
        domains: domainsWithProducts.filter((domain) => domain.botZapping || domain.paidMarketing),
      },
      {
        onSuccess: () => {
          onOpenChange(false)
        },
      }
    )
  }

  const handleSetProducts =
    (address: string) => (products: { paidMarketing?: boolean; botZapping?: boolean }) => {
      setDomainsWithProducts((prev) =>
        [
          // if domain already exists, remove it to avoid duplicates
          ...prev.filter((domain) => domain.address !== address),
          {
            paidMarketing: products.paidMarketing || false,
            botZapping: products.botZapping || false,
            address,
          },
        ].sort(sortDomains)
      )
    }

  return (
    <DomainsToProductsContainer>
      <DomainsToProductsCard>
        <DomainsToProductsCardRow isHeader>
          <StyledText css={{ flex: 1 }} variant={'baseBold'}>
            {t('add_new_domain_modal.domain')}
          </StyledText>
          <StyledText css={{ flex: 1 }} variant={'baseBold'}>
            {t('add_new_domain_modal.products')}
          </StyledText>
        </DomainsToProductsCardRow>
        {domainsWithProducts.map((domain) => (
          <DomainsToProductsCardRow key={domain.address}>
            <StyledText css={{ flex: 1, wordBreak: 'break-all', paddingRight: '15px' }}>
              {domain.address}
            </StyledText>
            <StyledText css={{ flex: 1 }}>
              <SelectProduct
                onSelect={handleSetProducts(domain.address)}
                selected={{ paidMarketing: domain.paidMarketing, botZapping: domain.botZapping }}
                disabledProducts={blockedDomains}
                showOnlySelect
              />
            </StyledText>
          </DomainsToProductsCardRow>
        ))}
      </DomainsToProductsCard>
      {productsLimitExceeded && (
        <MessageBoxContainer>
          <Toast type={'warning'}>
            <StyledText variant={'baseLight'}>
              <Trans
                ns={'domainManagement'}
                i18nKey={'add_new_domain_modal.exceeded_product_limit'}
                values={{ product: paidMarketingLimitExceeded ? 'Paid Marketing' : 'Bot Zapping' }}
              />
            </StyledText>
          </Toast>
        </MessageBoxContainer>
      )}
      <ModalControlsStyled css={{ borderTop: 'none' }}>
        <Button
          variant="ghost"
          text={t('add_new_domain_modal.cancel')}
          onClick={() => {
            onOpenChange(0)
          }}
        />
        <ButtonWithIcon
          iconLeft={
            <Plus
              stroke="#ffffff"
              fill={
                noDomainsSelected || productsLimitExceeded
                  ? 'var(--colors-lightNeutral)'
                  : 'var(--colors-primaryColor)'
              }
            />
          }
          disabled={noDomainsSelected || productsLimitExceeded}
          text={t('add_new_domain_modal.finish')}
          loading={isLoading}
          onClick={addDomains}
        />
      </ModalControlsStyled>
    </DomainsToProductsContainer>
  )
}
